/**
 * KanbanCard — Componente independiente para tarjetas Kanban (0259)
 * Punto 3: extracción. Punto 4: AppointmentCard (servicio, fecha, profesional).
 * Punto 6: TicketCard para tickets sin cita (nombre, teléfono, último mensaje).
 * Variantes: hasAppointment → AppointmentCardContent; sino → TicketCardContent.
 */
import React from "react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { makeStyles } from "@material-ui/core/styles";
import { Card, CardContent, Avatar, IconButton, Typography, Chip } from "@material-ui/core";
import { Chat as ChatIcon, Schedule as ScheduleIcon, DragIndicator as DragIcon, Event as EventIcon } from "@material-ui/icons";
import { getInitials } from "../../helpers/getInitials";
import { generateColor } from "../../helpers/colorGenerator";
import { getProfilePicUrl } from "../../services/api";
import { cardSurface, liftHover } from "../../theme/redesign";

function formatAppointmentDate(date) {
  if (!date) return "";
  try {
    const d = typeof date === "string" ? parseISO(date) : new Date(date);
    return format(d, "d MMM - h a", { locale: es });
  } catch {
    return String(date);
  }
}

// 0259 UX: Maqueta compacta — fuentes pequeñas, tarjetas sutiles
const useStyles = makeStyles((theme) => ({
  card: {
    ...cardSurface(theme),
    ...liftHover(theme),
    marginBottom: theme.spacing(1),
    cursor: "grab",
    "&:active": {
      cursor: "grabbing",
    },
  },
  cardContent: {
    padding: theme.spacing(1),
    "&:last-child": {
      paddingBottom: theme.spacing(1),
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
    gap: theme.spacing(0.5),
  },
  cardHeaderLeft: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    gap: theme.spacing(0.5),
  },
  cardHeaderIcons: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    flexShrink: 0,
  },
  avatar: {
    width: 28,
    height: 28,
    marginRight: theme.spacing(0.75),
    fontSize: "0.75rem",
    backgroundColor: theme.palette.primary.main,
    border: `1px solid ${theme.palette.divider}`,
  },
  contactInfo: {
    flex: 1,
    minWidth: 0,
  },
  contactName: {
    fontWeight: 600,
    fontSize: "0.8rem",
    lineHeight: 1.3,
    marginBottom: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  contactNumber: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  },
  lastMessage: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
    marginTop: theme.spacing(0.25),
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  },
  // 0261 maqueta: serviceRow con icons alineados a la derecha
  serviceRowWithIcons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.25),
    flexWrap: "wrap",
  },
  serviceDateGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
    flex: 1,
    minWidth: 0,
  },
  quickActions: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    flexShrink: 0,
  },
  actionButton: {
    padding: 2,
    minWidth: 28,
    height: 28,
    "& .MuiSvgIcon-root": { fontSize: "0.9rem" },
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  dragIcon: {
    color: theme.palette.text.disabled,
    fontSize: "0.85rem",
    marginRight: theme.spacing(0.5),
  },
  // 0259 Punto 4: AppointmentCard (maqueta compacta)
  serviceRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.25),
    flexWrap: "wrap",
  },
  serviceName: {
    fontWeight: 600,
    fontSize: "0.8rem",
    color: theme.palette.text.primary,
    lineHeight: 1.3,
  },
  dateRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  professionalChip: {
    height: 22,
    fontSize: "0.7rem",
    "& .MuiChip-label": { padding: "0 6px" },
  },
  professionalChipRow: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(0.5),
  },
}));

function truncateText(text, maxLength = 50) {
  if (!text) return "";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
}

function getContactAvatar(contact) {
  if (!contact) return null;
  if (
    contact.profilePicUrl &&
    contact.profilePicUrl !== "" &&
    !contact.profilePicUrl.includes("nopicture.png")
  ) {
    return getProfilePicUrl(contact.profilePicUrl);
  }
  return null;
}

/** 0261 maqueta: TicketCard con icons a la derecha. */
function TicketCardContent({ ticket, classes, actionIcons }) {
  return (
    <div className={classes.serviceRowWithIcons}>
      <Typography variant="body2" className={classes.lastMessage} style={{ flex: 1, minWidth: 0 }}>
        {truncateText(ticket.lastMessage, 50)}
      </Typography>
      {actionIcons}
    </div>
  );
}

/** 0261 maqueta PROPUESTA: servicio + fecha en línea, icons (reloj, chat) alineados a la derecha. */
function AppointmentCardContent({ serviceName, appointmentDate, professionalName, classes, actionIcons }) {
  return (
    <>
      <div className={classes.serviceRowWithIcons}>
        <div className={classes.serviceDateGroup}>
          {serviceName && (
            <Typography variant="body2" className={classes.serviceName}>
              {serviceName}
            </Typography>
          )}
          {appointmentDate && (
            <div className={classes.dateRow}>
              <EventIcon fontSize="small" />
              {formatAppointmentDate(appointmentDate)}
            </div>
          )}
        </div>
        {actionIcons}
      </div>
      {professionalName && (
        <div className={classes.professionalChipRow}>
          <Chip
            label={professionalName}
            size="small"
            color="primary"
            variant="outlined"
            className={classes.professionalChip}
          />
        </div>
      )}
    </>
  );
}

export default function KanbanCard({
  ticket,
  isMoving,
  isDragging,
  onCardClick,
  onCalendarClick,
}) {
  const classes = useStyles();

  const hasAppointment =
    ticket.currentAppointmentId ||
    (ticket.appointment && (ticket.appointment.serviceName || ticket.appointment.appointmentDate || ticket.appointment.professionalName));

  const serviceName = ticket.appointment?.serviceName ?? ticket.serviceName ?? null;
  const appointmentDate = ticket.appointment?.appointmentDate ?? ticket.appointmentDate ?? null;
  const professionalName = ticket.appointment?.professionalName ?? ticket.professionalName ?? null;

  const handleAvatarError = (event) => {
    event.target.style.display = "none";
  };

  return (
    <Card
      className={classes.card}
      style={{
        transform: isDragging ? "rotate(5deg)" : "none",
        boxShadow: isDragging ? "0 5px 10px rgba(0,0,0,0.15)" : "none",
        opacity: isMoving ? 0.6 : 1,
        pointerEvents: isMoving ? "none" : "auto",
        transition: "all 0.3s ease",
        border: isDragging ? "2px solid #1976d2" : undefined,
      }}
    >
      <CardContent className={classes.cardContent}>
        {/* 0261 maqueta: Header compacto — drag, avatar, nombre */}
        <div className={classes.cardHeader}>
          <div className={classes.cardHeaderLeft}>
            <DragIcon className={classes.dragIcon} />
            <Avatar
              className={`${classes.avatar}${!getContactAvatar(ticket.contact) ? " avatar-no-photo" : ""}`}
              src={getContactAvatar(ticket.contact)}
              onError={handleAvatarError}
              style={{
                backgroundColor: generateColor(ticket.contact?.number),
                color: "white",
                fontWeight: "bold",
              }}
            >
              {getInitials(ticket.contact?.name) || "?"}
            </Avatar>
            <div className={classes.contactInfo}>
              <Typography variant="body2" className={classes.contactName}>
                {ticket.contact?.name || "Sin contacto"}
              </Typography>
              {!hasAppointment && (
                <Typography variant="caption" className={classes.contactNumber}>
                  {ticket.contact?.number || "N/A"}
                </Typography>
              )}
            </div>
          </div>
        </div>

        {/* 0261 maqueta PROPUESTA: servicio/fecha + icons (reloj→Calendario, chat) alineados a la derecha */}
        {(() => {
          const actionIcons = (
            <div className={classes.quickActions}>
              <IconButton
                className={classes.actionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onCalendarClick?.(e);
                }}
                title="Ver calendario Agenda"
              >
                <ScheduleIcon fontSize="small" />
              </IconButton>
              <IconButton
                className={classes.actionButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onCardClick?.(ticket);
                }}
                title="Ver conversación"
              >
                <ChatIcon fontSize="small" />
              </IconButton>
            </div>
          );
          return hasAppointment ? (
            <AppointmentCardContent
              serviceName={serviceName}
              appointmentDate={appointmentDate}
              professionalName={professionalName}
              classes={classes}
              actionIcons={actionIcons}
            />
          ) : (
            <TicketCardContent ticket={ticket} classes={classes} actionIcons={actionIcons} />
          );
        })()}
      </CardContent>
    </Card>
  );
}
